import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Plus, Pencil, Trash2, CheckCircle, XCircle, Star } from 'lucide-react';
import './styles/customer-addresses.css';
import { useConfirm } from './components/ui/ConfirmDialog';

const EMPTY_FORM = {
  type: 'shipping',
  firstName: '',
  lastName: '',
  company: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'Nigeria',
  phone: '',
  isDefault: false
};

const CustomerAddresses = () => {
  const { confirm } = useConfirm();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchAddresses = useCallback(async () => {
    try {
      const response = await fetch('/api/customer/addresses', {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAddresses(data.addresses || []);
      } else {
        throw new Error('Failed to fetch addresses');
      }
    } catch (err) {
      setError('Failed to load addresses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAddresses(); }, [fetchAddresses]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingAddress(null);
    setFormData(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      const url = editingAddress
        ? `/api/customer/addresses/${editingAddress.id}`
        : '/api/customer/addresses';
      const method = editingAddress ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setSuccess(editingAddress ? 'Address updated' : 'Address added');
        setTimeout(() => setSuccess(''), 3000);
        resetForm();
        fetchAddresses();
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save address');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setFormData({
      type:       address.type,
      firstName:  address.firstName,
      lastName:   address.lastName,
      company:    address.company    || '',
      address1:   address.address1,
      address2:   address.address2   || '',
      city:       address.city,
      state:      address.state,
      postalCode: address.postalCode,
      country:    address.country,
      phone:      address.phone,
      isDefault:  address.isDefault
    });
    setShowForm(true);
  };

  const handleDelete = async (addressId) => {
    const confirmed = await confirm('Delete this address? This cannot be undone.', {
      title: 'Delete address',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    if (!confirmed) return;
    try {
      const response = await fetch(`/api/customer/addresses/${addressId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (response.ok) {
        setSuccess('Address deleted');
        setTimeout(() => setSuccess(''), 3000);
        fetchAddresses();
      } else {
        throw new Error('Failed to delete address');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="caddr">
        <div className="caddr__skel caddr__skel--head" />
        <div className="caddr__skel caddr__skel--block" />
        <div className="caddr__skel caddr__skel--block" />
      </div>
    );
  }

  return (
    <div className="caddr">
      <header className="caddr__head">
        <div className="caddr__head-text">
          <h1 className="caddr__title">Addresses</h1>
          <p className="caddr__sub">Manage your shipping and billing addresses.</p>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="caddr__btn caddr__btn--primary"
          >
            <Plus size={14} aria-hidden /> Add address
          </button>
        )}
      </header>

      {error && (
        <div className="caddr__alert caddr__alert--error" role="alert">
          <XCircle size={14} aria-hidden /> <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="caddr__alert caddr__alert--success" role="status">
          <CheckCircle size={14} aria-hidden /> <span>{success}</span>
        </div>
      )}

      {showForm && (
        <section className="caddr__card" aria-label={editingAddress ? 'Edit address' : 'Add address'}>
          <header className="caddr__card-head">
            <h2 className="caddr__card-title">
              {editingAddress ? 'Edit address' : 'Add new address'}
            </h2>
          </header>
          <form onSubmit={handleSubmit} className="caddr__form">
            <div className="caddr__row">
              <label className="caddr__field">
                <span className="caddr__label">Type</span>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                  className="caddr__input"
                >
                  <option value="shipping">Shipping</option>
                  <option value="billing">Billing</option>
                </select>
              </label>
              <label className="caddr__field caddr__field--check">
                <input
                  type="checkbox"
                  name="isDefault"
                  checked={formData.isDefault}
                  onChange={handleInputChange}
                />
                <span>Set as default</span>
              </label>
            </div>

            <div className="caddr__row">
              <label className="caddr__field">
                <span className="caddr__label">First name *</span>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="caddr__input"
                />
              </label>
              <label className="caddr__field">
                <span className="caddr__label">Last name *</span>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="caddr__input"
                />
              </label>
            </div>

            <label className="caddr__field">
              <span className="caddr__label">Company <span className="caddr__opt">(optional)</span></span>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className="caddr__input"
              />
            </label>

            <label className="caddr__field">
              <span className="caddr__label">Address line 1 *</span>
              <input
                type="text"
                name="address1"
                value={formData.address1}
                onChange={handleInputChange}
                required
                className="caddr__input"
              />
            </label>

            <label className="caddr__field">
              <span className="caddr__label">Address line 2 <span className="caddr__opt">(optional)</span></span>
              <input
                type="text"
                name="address2"
                value={formData.address2}
                onChange={handleInputChange}
                className="caddr__input"
              />
            </label>

            <div className="caddr__row caddr__row--3">
              <label className="caddr__field">
                <span className="caddr__label">City *</span>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  className="caddr__input"
                />
              </label>
              <label className="caddr__field">
                <span className="caddr__label">State *</span>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  required
                  className="caddr__input"
                />
              </label>
              <label className="caddr__field">
                <span className="caddr__label">ZIP *</span>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  required
                  className="caddr__input"
                />
              </label>
            </div>

            <div className="caddr__row">
              <label className="caddr__field">
                <span className="caddr__label">Country *</span>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                  className="caddr__input"
                >
                  <option value="Nigeria">Nigeria</option>
                  <option value="Ghana">Ghana</option>
                  <option value="Kenya">Kenya</option>
                  <option value="South Africa">South Africa</option>
                </select>
              </label>
              <label className="caddr__field">
                <span className="caddr__label">Phone *</span>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="caddr__input"
                />
              </label>
            </div>

            <div className="caddr__actions">
              <button
                type="button"
                onClick={resetForm}
                className="caddr__btn caddr__btn--ghost"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="caddr__btn caddr__btn--primary"
                disabled={submitting}
              >
                {submitting ? 'Saving…' : (editingAddress ? 'Update address' : 'Add address')}
              </button>
            </div>
          </form>
        </section>
      )}

      {addresses.length === 0 && !showForm ? (
        <div className="caddr__empty">
          <MapPin size={32} aria-hidden />
          <div>
            <div className="caddr__empty-title">No addresses yet</div>
            <div className="caddr__empty-sub">Add a shipping or billing address to speed up checkout.</div>
          </div>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="caddr__btn caddr__btn--primary"
          >
            <Plus size={14} aria-hidden /> Add your first address
          </button>
        </div>
      ) : (
        <ul className="caddr__list">
          {addresses.map(address => (
            <li
              key={address.id}
              className={`caddr-card ${address.isDefault ? 'is-default' : ''}`}
            >
              <header className="caddr-card__head">
                <span className={`caddr-pill ${address.type === 'shipping' ? 'caddr-pill--shipping' : 'caddr-pill--billing'}`}>
                  {address.type === 'shipping' ? 'Shipping' : 'Billing'}
                </span>
                {address.isDefault && (
                  <span className="caddr-card__default">
                    <Star size={12} aria-hidden /> Default
                  </span>
                )}
              </header>
              <address className="caddr-card__body">
                <div className="caddr-card__name">{address.firstName} {address.lastName}</div>
                {address.company && <div>{address.company}</div>}
                <div>{address.address1}</div>
                {address.address2 && <div>{address.address2}</div>}
                <div>{address.city}, {address.state} {address.postalCode}</div>
                <div>{address.country}</div>
                <div className="caddr-card__phone">{address.phone}</div>
              </address>
              <footer className="caddr-card__actions">
                <button
                  type="button"
                  onClick={() => handleEdit(address)}
                  className="caddr__btn caddr__btn--ghost caddr__btn--sm"
                >
                  <Pencil size={14} aria-hidden /> Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(address.id)}
                  className="caddr__btn caddr__btn--danger caddr__btn--sm"
                >
                  <Trash2 size={14} aria-hidden /> Delete
                </button>
              </footer>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CustomerAddresses;
